import React, { createContext, useContext, useReducer } from "react";

const CartContext = createContext({});

const cartReducer = (state, action) => {
  switch (action.type) {
    case "ADD_ITEM": {
      const { item, restaurantId, restaurantName } = action.payload;
      // Use cartKey (which includes size) for deduplication
      const key = item.cartKey || item.id;

      // Different restaurant — clear cart
      if (state.restaurantId && state.restaurantId !== restaurantId) {
        return {
          restaurantId,
          restaurantName,
          items: [{ ...item, cartKey: key, quantity: 1 }],
        };
      }
      const existing = state.items.find((i) => (i.cartKey || i.id) === key);
      if (existing) {
        return {
          ...state,
          restaurantId,
          restaurantName,
          items: state.items.map((i) =>
            (i.cartKey || i.id) === key ? { ...i, quantity: i.quantity + 1 } : i
          ),
        };
      }
      return {
        ...state,
        restaurantId,
        restaurantName,
        items: [...state.items, { ...item, cartKey: key, quantity: 1 }],
      };
    }

    case "REMOVE_ITEM":
      return {
        ...state,
        items: state.items.filter((i) => (i.cartKey || i.id) !== action.payload),
      };

    case "UPDATE_QUANTITY": {
      const { id, quantity } = action.payload;
      if (quantity <= 0) {
        return { ...state, items: state.items.filter((i) => (i.cartKey || i.id) !== id) };
      }
      return {
        ...state,
        items: state.items.map((i) =>
          (i.cartKey || i.id) === id ? { ...i, quantity } : i
        ),
      };
    }

    case "CLEAR_CART":
      return { restaurantId: null, restaurantName: "", items: [] };

    default:
      return state;
  }
};

const initialState = { restaurantId: null, restaurantName: "", items: [] };

export const CartProvider = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, initialState);

  const addItem = (item, restaurantId, restaurantName) =>
    dispatch({ type: "ADD_ITEM", payload: { item, restaurantId, restaurantName } });

  const removeItem = (cartKey) =>
    dispatch({ type: "REMOVE_ITEM", payload: cartKey });

  const updateQuantity = (cartKey, quantity) =>
    dispatch({ type: "UPDATE_QUANTITY", payload: { id: cartKey, quantity } });

  const clearCart = () => dispatch({ type: "CLEAR_CART" });

  const totalItems = cart.items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = cart.items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ cart, addItem, removeItem, updateQuantity, clearCart, totalItems, totalPrice }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
};
